<template>
  <div id="main-content">
    <SuccessBanner :header="referenceData.keepMyCoverHeading.informationText">
      <div v-html="referenceData.keepMyCoverSubtitle.informationText" />
    </SuccessBanner>
    <div class="mid-content fr-view">
      <h2 class="h3">{{ referenceData.keepMyCoverSubheading.informationText }}</h2>

      <div class="selected-product-card">
        <div class="product-info fr-view">
          <h3 class="h4">
            {{ myRecommendations.existingProducts.existingProductName }}
          </h3>
          <ul>
            <li>
              ${{ currentExcess }}
              <template v-if="isMemberExcessProduct">Hospital excess is payable once per adult per financial year</template>
              <template v-else>Hospital excess is payable once per policy per financial year</template>
            </li>
            <li v-html="referenceData.keepMyCoverSupportText.informationText"></li>
          </ul>
        </div>
        <div class="premium-info">
          <h3 class="h4">Your premium</h3>
          <p v-if="premium !== null" class="premium-payment-info">
            <span class="premium-price">{{ premium }}</span>
            / {{ paymentFrequency }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { mapMutations } from 'vuex';
import { SuccessBanner } from '@/components';
import { ProductType } from '@general/js/enums';
import { ProductExcessType } from '@/enums';
import { VUEX_CHANGE_COVER_ROOT, VUEX_CAPTURE_ADDITIONAL_INFORMATION, VUEX_CHANGE_PRODUCT } from '@/constants/ChangeCover/ChangeCoverVuexConstants';
import { CHANGE_MY_COVER } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import ChangeCoverMixins from './ChangeCoverMixins';

export default {
  name: 'KeepMyCover',

  components: {
    SuccessBanner,
  },

  props: {
    currentPremium: {
      type: String,
      default: null,
    },
  },

  mixins: [ChangeCoverMixins],

  beforeMount() {
    this.currentPaidToDate = this.getPaidToDate();
    this.premium = this.currentPremium;
  },

  data() {
    return {
      currentPaidToDate: null,
      premium: null,
    };
  },

  methods: {
    format,
    ...mapMutations('ChangeCoverRoot', {
      resetData: VUEX_CHANGE_COVER_ROOT.MUTATIONS.resetData,
    }),
    ...mapMutations('ChangeCoverRoot/CaptureAdditionalInformation', {
      resetCAIData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.resetCAIData,
    }),
    ...mapMutations('ChangeCoverRoot/ChangeProduct', {
      resetChangeCoverData: VUEX_CHANGE_PRODUCT.MUTATIONS.resetChangeCoverData,
    }),

    // Wipe off data from vuex
    clearData() {
      this.resetData();
      this.resetCAIData();
      this.resetChangeCoverData();
    },

    redirectToChangeCover() {
      this.clearData();
      setNavigationState({
        getInitialCRMValues: true,
      });
      this.$router.push({
        name: CHANGE_MY_COVER,
      });
    },
  },

  computed: {
    isExtrasOnly() {
      return !this.myRecommendations.existingProducts.products.some((product) => {
        return product.productType.toLowerCase() !== ProductType.EXTRAS_ONLY;
      });
    },

    isMemberExcessProduct() {
      const foundItemIndex = this.myRecommendations.existingProducts.products.findIndex((product) => {
        return product.productExcessType === ProductExcessType.MEMBER_EXCESS;
      });

      if (foundItemIndex === -1) return false;
      return true;
    },

    breadcrumb() {
      const breadcrumb = [
        {
          text: 'Change my cover',
          navigate: CHANGE_MY_COVER,
        },
        {
          text: 'Keep my cover',
          disabled: true,
        },
      ];

      return breadcrumb;
    },
  },
};
</script>

<style lang="scss" scoped>
.mid-content {
  @include flex-col;
  padding: rem(56) 0;

  &:before {
    content: '';
    height: 100%;
    width: 100vw;
    position: absolute;
    top: 0;
    right: 50%;
    transform: translateX(50%);
    z-index: -1;
    background-color: $GREY_25;
  }

  .selected-product-card {
    @include flex-row;

    padding: 2rem 1rem;
    justify-content: space-between;
    margin: 1rem 0;
    column-gap: 1.5rem;
    background-color: $WHITE;
    border-radius: 16px;
    box-shadow: 0 2px 6px 0 rgba(22,22,29,.06),0 2px 4px 0 rgba(22,22,29,.02);

    @include mobile {
      @include flex-col;
    }

    .product-info {
      padding: 0 1.5rem;
      @include mobile {
        padding-bottom: 2rem;

        ul {
          margin-bottom: 0;

          li:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .premium-info {
      border-left: 1px solid $GREY_300;
      padding: 0 1.5rem;

      @include mobile {
        border-left: unset;
        padding-left: unset;
        border-top: 1px solid $GREY_300;
        padding-top: 2rem;
      }

      .premium-payment-info {
        font-size: 18px;
        font-weight: $BOLD;
        color: $PRIMARY_GREEN_100;

        .premium-price {
          font-size: 31px;
        }
      }
    }
  }

  h2 {
    margin-bottom: 2rem;
  }
}

:deep(.call-out) {
  height: max-content;
  justify-self: end !important;
  max-width: 330px;
}

:deep(.success-banner) {
  margin-bottom: 0;
}
</style>
